import { forwardRef } from "react";

import { ExternalLink } from "lucide-react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { ComponentSpinner } from "@components/spinner";
import { formatDate } from "@helpers/date";
import { NOOK_PAY_BANK_DETAILS, NOOK_PAY_ENTITY } from "@src/utility/constants/nook-pay-entity";
import { formatCurrency } from "@src/utility/helpers/utils";
import {
    DEBIT_NOTE_AGREEMENT_NAME,
    DEBIT_NOTE_PAYMENT_TERMS,
    DEBIT_NOTE_SUPPORTING_DOCUMENTS,
    getDebitNoteTerms
} from "../misc/constants";
import { getFinancialYear } from "../misc/helpers";

const Section = ({ title, children, className }) => (
    <section className={classnames("mt-6 break-inside-avoid", className)}>
        <h3 className="border-b border-gray-200 pb-1.5 text-sm font-bold uppercase tracking-wide text-navy">{title}</h3>
        <div className="mt-3">{children}</div>
    </section>
);

Section.propTypes = {
    title    : PropTypes.string.isRequired,
    children : PropTypes.node,
    className: PropTypes.string
};

const DetailRow = ({ label, value }) => (
    <tr className="border-b border-gray-100 last:border-0">
        <td className="w-48 py-1.5 pr-4 text-muted">{label}</td>
        <td className="py-1.5 text-gray-900">{value || "—"}</td>
    </tr>
);

DetailRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.node
};

const TH = "border border-gray-200 p-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted";
const TD = "border border-gray-200 p-2 align-top";

/**
 * The debit note as a document, laid out on the India debit note template:
 * issuer letterhead, document details, bill-to, purpose, vendor payout lines,
 * summary, terms, supporting documents, signatory and declaration. Forwarded
 * ref points at the page so it can be printed as-is.
 */
const DebitNoteDocument = forwardRef(({ note, brand, lines, totals, loading }, ref) => {
    const currency = note.currency;
    const issuer = NOOK_PAY_ENTITY.name;
    const brandName = brand?.name || "the brand";
    const issuedOn = note.issued_at || note.createdAt;
    const paymentTerms = note.due_date ? `by ${formatDate(note.due_date)}` : DEBIT_NOTE_PAYMENT_TERMS;
    const agreementDate = note.agreement_date ? ` dated ${formatDate(note.agreement_date)}` : "";
    const money = (value) => formatCurrency(value, currency);
    const terms = getDebitNoteTerms({ issuer, brand: brandName, netAmount: money(totals.net), paymentTerms });

    return (
        <div ref={ref} className="bg-white text-sm text-gray-800">
            <div className="flex items-start justify-between gap-6 border-b-2 border-navy pb-4">
                <div>
                    <p className="text-lg font-bold uppercase text-navy">{issuer}</p>
                    <div className="mt-1 space-y-0.5 text-xs text-muted">
                        {NOOK_PAY_ENTITY.gstin && <p>GSTIN: {NOOK_PAY_ENTITY.gstin}</p>}
                        {NOOK_PAY_ENTITY.pan && <p>PAN: {NOOK_PAY_ENTITY.pan}</p>}
                        {NOOK_PAY_ENTITY.address && <p>Address: {NOOK_PAY_ENTITY.address}</p>}
                        {NOOK_PAY_ENTITY.phone && <p>Phone: {NOOK_PAY_ENTITY.phone}</p>}
                        {NOOK_PAY_ENTITY.email && <p>Email: {NOOK_PAY_ENTITY.email}</p>}
                    </div>
                </div>
                <p className="shrink-0 text-2xl font-bold tracking-tight text-gray-900">DEBIT NOTE</p>
            </div>

            <Section title="Document Details">
                <table className="w-full">
                    <tbody>
                        <DetailRow label="Debit Note No." value={note.debit_note_number} />
                        <DetailRow label="Date of Issue" value={formatDate(issuedOn)} />
                        <DetailRow label="Financial Year" value={getFinancialYear(issuedOn)} />
                        <DetailRow label="Payout Period" value={note.payout_period ? formatDate(note.payout_period, "MMMM YYYY") : null} />
                        <DetailRow label="Reference" value={note.reference || note.agreement_reference} />
                    </tbody>
                </table>
            </Section>

            <Section title="Bill To / Issued To">
                <p className="font-semibold uppercase text-gray-900">{brandName}</p>
                <div className="mt-1 space-y-0.5 text-xs text-muted">
                    <p>GSTIN: {brand?.gstin || "—"}</p>
                    <p>Address: {brand?.address || "—"}</p>
                    <p>Contact: {brand?.email || "—"}</p>
                </div>
            </Section>

            <Section title="Nature and Purpose of Debit Note">
                <p className="leading-relaxed">
                    This Debit Note is issued for <span className="font-medium">reimbursement of vendor payouts facilitated by {issuer} as Pure Agent</span> of {brandName} under the {DEBIT_NOTE_AGREEMENT_NAME}{agreementDate}.
                </p>
                <p className="mt-2 leading-relaxed">
                    <span className="font-medium">Purpose:</span> To document and record the payout of vendor / publisher invoices made by {issuer} on behalf and in the name of {brandName}, as pure agent under Rule 33 of the CGST Rules, 2017.
                </p>
            </Section>

            <Section title="Details of Vendor Payouts Facilitated" className="break-inside-auto">
                {loading ? (
                    <div className="flex justify-center py-6">
                        <ComponentSpinner />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-xs">
                            <thead className="bg-light">
                                <tr>
                                    <th className={TH}>S. No.</th>
                                    <th className={TH}>Vendor / Publisher Name</th>
                                    <th className={TH}>Vendor GSTIN</th>
                                    <th className={TH}>Invoice No.</th>
                                    <th className={TH}>Invoice Date</th>
                                    <th className={classnames(TH, "text-right")}>Invoice Amount (incl. GST)</th>
                                    <th className={classnames(TH, "text-right")}>TDS Deducted</th>
                                    <th className={classnames(TH, "text-right")}>Net Amount Paid</th>
                                    <th className={TH}>Reference</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map((line, idx) => (
                                    <tr key={line.id || idx} className="break-inside-avoid">
                                        <td className={TD}>{idx + 1}</td>
                                        <td className={classnames(TD, "text-gray-900")}>{line.vendorName}</td>
                                        <td className={TD}>{line.vendorGstin}</td>
                                        <td className={TD}>
                                            {line.invoiceUrl ? (
                                                <a
                                                    href={line.invoiceUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 text-gray-700 hover:text-primary hover:underline">
                                                    {line.invoiceNumber}
                                                    <ExternalLink className="h-3 w-3" />
                                                </a>
                                            ) : line.invoiceNumber}
                                        </td>
                                        <td className={classnames(TD, "whitespace-nowrap")}>{formatDate(line.invoiceDate)}</td>
                                        <td className={classnames(TD, "text-right")}>{formatCurrency(line.amount, line.currency || currency)}</td>
                                        <td className={classnames(TD, "text-right")}>{formatCurrency(line.tds, line.currency || currency)}</td>
                                        <td className={classnames(TD, "text-right")}>{formatCurrency(line.net, line.currency || currency)}</td>
                                        <td className={TD}>{line.reference}</td>
                                    </tr>
                                ))}
                                {lines.length === 0 && (
                                    <tr>
                                        <td colSpan={9} className={classnames(TD, "py-4 text-center text-muted")}>No vendor invoices on this debit note.</td>
                                    </tr>
                                )}
                                <tr className="bg-light font-semibold text-gray-900">
                                    <td colSpan={5} className={TD}>Total</td>
                                    <td className={classnames(TD, "text-right")}>{money(totals.amount)}</td>
                                    <td className={classnames(TD, "text-right")}>{money(totals.tds)}</td>
                                    <td className={classnames(TD, "text-right")}>{money(totals.net)}</td>
                                    <td className={TD} />
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </Section>

            <Section title="Debit Note Summary">
                <table className="w-full border-collapse">
                    <thead className="bg-light">
                        <tr>
                            <th className={TH}>Particulars</th>
                            <th className={classnames(TH, "w-48 text-right")}>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className={TD}>Total Vendor Payouts (including GST)</td>
                            <td className={classnames(TD, "text-right")}>{money(totals.amount)}</td>
                        </tr>
                        <tr>
                            <td className={TD}>Less: TDS Deducted by {brandName}</td>
                            <td className={classnames(TD, "text-right")}>{money(totals.tds)}</td>
                        </tr>
                        <tr>
                            <td className={TD}>Net Amount Claimed for Reimbursement</td>
                            <td className={classnames(TD, "text-right")}>{money(totals.net)}</td>
                        </tr>
                        <tr>
                            <td className={TD}>GST Charged by {issuer}</td>
                            <td className={classnames(TD, "text-right")}>{money(0)} (Nil)</td>
                        </tr>
                        <tr className="bg-navy/5 text-base font-bold text-navy">
                            <td className={TD}>Total Debit Note Value</td>
                            <td className={classnames(TD, "text-right")}>{money(totals.net)}</td>
                        </tr>
                    </tbody>
                </table>

                <div className="mt-4 rounded-lg border border-gray-200 bg-light p-4">
                    <p className="font-semibold text-gray-900">Bank details for payment</p>
                    <div className="mt-2 grid grid-cols-2 gap-y-1 text-xs">
                        <div className="text-muted">Beneficiary</div><div className="text-gray-900">{NOOK_PAY_BANK_DETAILS.beneficiary}</div>
                        <div className="text-muted">Bank name</div><div className="text-gray-900">{NOOK_PAY_BANK_DETAILS.bank_name}</div>
                        <div className="text-muted">Account number</div><div className="text-gray-900">{NOOK_PAY_BANK_DETAILS.account_number}</div>
                        <div className="text-muted">IFSC</div><div className="text-gray-900">{NOOK_PAY_BANK_DETAILS.ifsc}</div>
                    </div>
                </div>
            </Section>

            <Section title="Terms and Conditions / Basis of Debit Note" className="break-inside-auto">
                <ol className="list-decimal space-y-2 pl-5 leading-relaxed">
                    {terms.map((term) => (
                        <li key={term.title} className="break-inside-avoid">
                            <span className="font-medium text-gray-900">{term.title}:</span> {term.body}
                        </li>
                    ))}
                </ol>
            </Section>

            <Section title="Supporting Documents">
                <p>The following documents are attached / made available for reference:</p>
                <ul className="mt-2 space-y-1">
                    {DEBIT_NOTE_SUPPORTING_DOCUMENTS.map((item) => (
                        <li key={item} className="flex items-center gap-2">
                            <span className="h-3 w-3 shrink-0 rounded-sm border border-gray-400" />
                            {item}
                        </li>
                    ))}
                </ul>
            </Section>

            {/* <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Section title="Authorized Signatory" className="mt-0">
                    <div className="space-y-2">
                        <p className="font-medium text-gray-900">For {issuer}</p>
                        <p>Signature: <span className="inline-block w-40 border-b border-gray-400" /></p>
                        <p>Name: <span className="inline-block w-40 border-b border-gray-400" /></p>
                        <p>Title: <span className="inline-block w-40 border-b border-gray-400" /></p>
                        <p>Date: {formatDate(issuedOn)}</p>
                        <p>Stamp (if applicable):</p>
                    </div>
                </Section>

                <Section title="Declaration" className="mt-0">
                    <p className="leading-relaxed">
                        I / We hereby certify that the particulars mentioned in this Debit Note are true and correct and that this Debit Note has been issued in accordance with the provisions of the GST law and the terms of the agreement between {issuer} and {brandName}.
                    </p>
                    <p className="mt-4">Authorized Signatory: <span className="inline-block w-32 border-b border-gray-400" /></p>
                    <p className="mt-2">Date: {formatDate(issuedOn)}</p>
                </Section>
            </div> */}
        </div>
    );
});

DebitNoteDocument.displayName = "DebitNoteDocument";

DebitNoteDocument.propTypes = {
    note : PropTypes.object.isRequired,
    brand: PropTypes.shape({
        name   : PropTypes.string,
        gstin  : PropTypes.string,
        address: PropTypes.string,
        email  : PropTypes.string
    }),
    lines  : PropTypes.array.isRequired,
    totals : PropTypes.shape({ amount: PropTypes.number, tds: PropTypes.number, net: PropTypes.number }).isRequired,
    loading: PropTypes.bool
};

export default DebitNoteDocument;
